// src/services/paymentTerminal.ts
// Persistent Monri ECR WebSocket with CONFIRM in the *same* session.
// Keep API compatible with existing imports in Kiosk/PosRegister.

export type TerminalEvent = { type: "PROGRESS"; code?: string; message?: string };

type MonriApproved = { type: "APPROVED"; payload: any };
type MonriDeclined = { type: "DECLINED"; code?: string; message?: string };
type MonriError = { type: "ERROR"; code: string; message?: string; invoice?: string };
export type MonriResult = MonriApproved | MonriDeclined | MonriError;

type SendOptions = { expect?: string[]; timeoutMs?: number };

class MonriSocket {
  private static inst: MonriSocket | null = null;
  private ws: WebSocket | null = null;
  private url: string;
  private waiters: Array<{ resolve: (v: string) => void; reject: (e: any) => void }> = [];

  private constructor(host: string, port: number, path?: string) {
    //eslint-disable-next-line
    const proto = (typeof location !== "undefined" && location.protocol === "https:") ? "wss" : "ws";
    const p = (path || "/ecr").replace(/^\//, "/");
    this.url = `${proto}://${host}:${port}${p}`;
  }
  static get(host: string, port: number, path?: string) {
    if (!this.inst) this.inst = new MonriSocket(host, port, path);
    return this.inst;
  }

  async connect(): Promise<void> {
    if (this.ws && this.ws.readyState === WebSocket.OPEN) return;
    if (this.ws && this.ws.readyState === WebSocket.CONNECTING) {
      // wait a bit
      await new Promise((r) => setTimeout(r, 150));
      if (this.ws && this.ws.readyState === WebSocket.OPEN) return;
    }
    await new Promise<void>((resolve, reject) => {
      try {
        this.ws = new WebSocket(this.url);
        this.ws.onopen = () => resolve();
        this.ws.onerror = (e) => reject(e);
        this.ws.onmessage = (ev) => {
          const msg = String(ev.data ?? "");
          const waiter = this.waiters.shift();
          if (waiter) waiter.resolve(msg);
        };
        this.ws.onclose = () => {
          // drain waiters with error
          while (this.waiters.length) {
            const w = this.waiters.shift();
            w && w.reject(new Error("Monri socket closed."));
          }
        };
      } catch (e) {
        reject(e);
      }
    });
  }

  async send(line: string, opt: SendOptions = {}): Promise<string> {
    await this.connect();
    const ws = this.ws!;
    const res = new Promise<string>((resolve, reject) => {
      this.waiters.push({ resolve, reject });
    });
    ws.send(line + "\n");
    const timeout = opt.timeoutMs ?? 15000;
    return await Promise.race<string>([
      res,
      new Promise((_, rej) => setTimeout(() => rej(new Error("Monri timeout")), timeout)),
    ]);
  }
}

// ---------- helpers ----------
function progress(cb?: (e: TerminalEvent) => void, code?: string, message?: string) {
  cb?.({ type: "PROGRESS", code, message });
}

async function getWsHostPort(): Promise<{ host: string; port: number; path?: string; merchant: string }> {
  const host = localStorage.getItem("monry_ws_host") || "127.0.0.1";
  const port = Number(localStorage.getItem("monry_ws_port") || "8080");
  const path = localStorage.getItem("monry_ws_path") || "/ecr";
  const merchant = localStorage.getItem("monry_merchant") || "87670018";
  return { host, port, path, merchant };
}

function parseProgressCode(line: string): string | undefined {
  // 001|...|<code>|message
  const m = line.match(/^\d{3}\|[^\|]*\|(\d+)\|/);
  return m?.[1];
}
function progressText(code?: string) {
  switch (code) {
    case "3": return "Molimo potvrdite na terminalu…";
    case "4": return "Čekamo odgovor izdavaoca…";
    case "17": return "Kartica očitana";
    case "18": return "Štampanje na terminalu…";
    default: return "Obrada…";
  }
}
function parseApproved(line: string) { return { raw: line }; }
function parseInvoiceFrom1001(line: string): string | null {
  // Matches ...|1001|...|<invoice>|...
  const m = line.match(/\|1001\|[^|]*\|(\d{3,})\|/);
  return m ? m[1] : null;
}

function build010(amount: number, ref: string, merchant: string): string {
  // This builder must be aligned with your terminal format.
  // We keep most fields blank except currency/amount/ref as per your logs.
  // Amount is BAM in cents: 10.50 -> "1050"
  const cents = Math.round(amount * 100);
  // Minimal safe frame per log: 87670018|010|0|100|||||BA|||||||
  return `${merchant}|010|0|100|||||BA|||||||${cents}|${ref}`;
}
function build030(invoice: string, approve: boolean, merchant: string): string {
  return `${merchant}|030|${invoice}|${approve ? "1" : "0"}`;
}

// ---------- public API ----------

/** Healthcheck; verifies that the WS can open and respond. */
export async function probeMonry(): Promise<boolean> {
  try {
    const { host, port, path } = await getWsHostPort();
    const sock = MonriSocket.get(host, port, path);
    await sock.connect();
    await sock.send("PING", { timeoutMs: 2000 });
    return true;
  } catch {
    return false;
  }
}

/** Main SALE (010). Returns MonriResult wrapped in { result }. */
export async function startCardSale(
  amount: number,
  ecrRef: string,
  onProgress?: (e: TerminalEvent) => void
): Promise<{ result: MonriResult }> {
  const { host, port, path, merchant } = await getWsHostPort();
  const sock = MonriSocket.get(host, port, path);

  const frame010 = build010(amount, ecrRef, merchant);
  progress(onProgress, "1", "Processing...");
  let line = await sock.send(frame010);

  // If terminal immediately responds with WAITING CONFIRM:
  if (line.includes("|1001|")) {
    const invoice = parseInvoiceFrom1001(line) || ecrRef;
    // Store pending to be robust across reloads
    localStorage.setItem("monry.pending.invoice", String(invoice));
    localStorage.setItem("monry.pending.amount", String(amount));
    return { result: { type: "ERROR", code: "1001", message: "Waiting CONFIRM", invoice } };
  }

  // Loop through progress until final 010|...|100|
  for (let guard = 0; guard < 80; guard++) {
    if (line.startsWith("001|") || line.startsWith("002|")) {
      const code = parseProgressCode(line);
      progress(onProgress, code, progressText(code));
      // wait next push or poll
      line = await sock.send("PING");
      continue;
    }
    if (line.startsWith("010|") && line.includes("|100|")) {
      // Approved
      localStorage.removeItem("monry.pending.invoice");
      localStorage.removeItem("monry.pending.amount");
      return { result: { type: "APPROVED", payload: parseApproved(line) } };
    }
    if (line.startsWith("010|") && (line.includes("|101|") || line.toLowerCase().includes("declined"))) {
      localStorage.removeItem("monry.pending.invoice");
      localStorage.removeItem("monry.pending.amount");
      return { result: { type: "DECLINED", message: "Declined" } };
    }
    // keep waiting
    line = await sock.send("PING");
  }

  return { result: { type: "ERROR", code: "TIMEOUT", message: "No final response" } };
}

/** CONFIRM / REJECT (030) — must go through the same socket. */
export async function confirmInvoice(invoice: string, approve: boolean): Promise<MonriResult> {
  const { host, port, path, merchant } = await getWsHostPort();
  const sock = MonriSocket.get(host, port, path);
  const frame030 = build030(invoice, approve, merchant);
  const r = await sock.send(frame030, { timeoutMs: 15000 });

  if (r.startsWith("030|") && r.includes("|1")) {
    localStorage.removeItem("monry.pending.invoice");
    localStorage.removeItem("monry.pending.amount");
    return { type: "APPROVED", payload: r };
  }
  if (r.startsWith("030|") && r.includes("|0")) {
    return { type: "DECLINED", message: "Rejected" };
  }
  return { type: "ERROR", code: "CONFIRM_FAIL", message: r };
}

/** Auto confirm by invoice/amount (simple strategy). */
export async function autoConfirmOrRejectByInvoice(invoice: string, expectedAmount: number): Promise<MonriResult> {
  // In future you can query last-amount and compare. For now we approve.
  return await confirmInvoice(invoice, true);
}